import axios from "axios";
import "../helpers/axios";
import {
  Recording,
  RecordingCategory,
  RecordingFiles,
} from "../model/recording";
import { Rubric } from "../model/rubric";

const API_BASE_URL = process.env.REACT_APP_API_URL ?? "";

const buildUrl = (path: string): string => `${API_BASE_URL}${path}`;

type ApiListEnvelope<T> = {
  data?: T;
  message?: string;
  lastKey?: string;
  nextPageToken?: string;
};

type RecordingListEnvelope = ApiListEnvelope<Recording[]> & {
  recordings?: Recording[];
};

type RecordingItemEnvelope = ApiListEnvelope<Recording> & {
  recording?: Recording;
};

type RubricListEnvelope = ApiListEnvelope<Rubric[]> & {
  rubrics?: Rubric[];
};

type FavoritesListEnvelope = ApiListEnvelope<Recording[]> & {
  favorites?: Recording[];
  recordings?: Recording[];
};

type FavoriteIDsListEnvelope = ApiListEnvelope<string[]> & {
  favoriteIDs?: string[];
  favoriteIds?: string[];
};

export interface ListRubricsResult {
  rubrics: Rubric[];
  lastKey?: string;
  message?: string;
}

export interface ListRecordingsParams {
  category?: RecordingCategory;
  rubricID?: string;
  from?: number;
  to?: number;
  limit?: number;
  nextPageToken?: string;
}

export interface ListRecordingsResult {
  recordings: Recording[];
  nextPageToken?: string;
  message?: string;
}

const normalizeArray = <T>(input: T[] | undefined): T[] =>
  Array.isArray(input) ? input : [];

export const listRubrics = async (): Promise<ListRubricsResult> => {
  const response = await axios.get<RubricListEnvelope>(buildUrl("/rubrics"));
  const payload = response.data;

  return {
    rubrics: normalizeArray(payload.data ?? payload.rubrics),
    lastKey: payload.lastKey,
    message: payload.message,
  };
};

export const listRecordings = async (
  params: ListRecordingsParams,
): Promise<ListRecordingsResult> => {
  const response = await axios.get<RecordingListEnvelope>(
    buildUrl("/recordings/filter"),
    {
      params: {
        category: params.category,
        rubricID: params.rubricID,
        from: params.from,
        to: params.to,
        limit: params.limit,
        nextPageToken: params.nextPageToken,
      },
    },
  );

  const payload = response.data;

  return {
    recordings: normalizeArray(payload.data ?? payload.recordings),
    nextPageToken: payload.nextPageToken,
    message: payload.message,
  };
};

export const listFavorites = async (): Promise<Recording[]> => {
  const response = await axios.get<FavoritesListEnvelope>(
    buildUrl("/favorites/"),
  );
  const payload = response.data;
  return normalizeArray(
    payload.data ?? payload.favorites ?? payload.recordings,
  );
};

export const listFavoriteIDs = async (): Promise<string[]> => {
  const response = await axios.get<FavoriteIDsListEnvelope>(
    buildUrl("/favorite-ids/"),
  );
  const payload = response.data;

  return normalizeArray(
    payload.data ?? payload.favoriteIDs ?? payload.favoriteIds,
  );
};

export const addFavorite = async (recordingID: string): Promise<void> => {
  await axios.post(buildUrl(`/favorites/${recordingID}/`));
};

export const removeFavorite = async (recordingID: string): Promise<void> => {
  await axios.delete(buildUrl(`/favorites/${recordingID}/`));
};

export const getRecordingFiles = async (
  recordingID: string,
): Promise<RecordingFiles> => {
  const response = await axios.get<RecordingFiles>(
    buildUrl(`/recordings/${recordingID}/files`),
  );
  return response.data;
};

export const getRecordingByID = async (
  recordingID: string,
): Promise<Recording | null> => {
  const response = await axios.get<RecordingItemEnvelope | Recording>(
    buildUrl(`/recordings/${recordingID}`),
  );

  const payload = response.data as RecordingItemEnvelope;
  if (payload && typeof payload === "object" && "recordingID" in payload) {
    return payload as unknown as Recording;
  }

  return payload.data ?? payload.recording ?? null;
};
